import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { FormGroup } from '@theneatcompany/nui'

import { useSignUp } from '@/components/Providers/SignUpProvider'
import Yup from '@/utils/yup'
import Form from '@/components/forms/Form'
import { Columns, Column, Rows } from '@/components/layout'
import InputField from '@/components/forms/InputField'
import CheckoutSidePassword from '@/components/blocks/CheckoutSidePassword'
import CheckoutSidePasswordNeatHome from '@/components/blocks/CheckoutSidePasswordNeatHome'
import PasswordInputField from '@/components/forms/PasswordInputField'
import PhoneNumberField from '@/components/forms/PhoneNumberField'
import { NEAT_HOME_TRIAL_SKU } from '@/constants'

import FormBox from './FormBox'
import SignUpLayout from './SignUpLayout'
import styles from './SignUp.module.scss'

const NewUser = () => {
  const { saveStepNewUser, user, plan } = useSignUp()

  const saveData = async (model) => {
    await saveStepNewUser(model)
  }

  const schema = Yup.object({
    firstName: Yup.string().trim().max(50).required(),
    lastName: Yup.string().trim().max(50).required(),
    phone: Yup.string().trim().phoneNumber({ path: 'phone' }).required(),
    password: user.googleId ? Yup.string()
      : Yup
        .string()
        .required()
        .disallowSpaces()
        .min(9)
        .hasLetterChars()
        .hasDigits(),
  })

  const FormError = () => {
    const { formState: { isSubmitted, errors } } = useFormContext()
    const { formatMessage } = useIntl()
    let errorString = ''
    if (!Object.keys(errors).length) return null

    const requiredErrors = Object.entries(errors).reduce((acc, [key, error]) => {
      if (error.type === 'required') {
        acc.push({
          ...error,
          label: `${formatMessage({ id: `label.${key}` })}`,
        })
      }
      return acc
    }, [])

    if (!requiredErrors.length) {
      const key = Object.keys(errors)[0]
      errorString = errors[key].message

      if (key === 'password' && !user.googleId) {
        errorString = 'Passwords must be at least 9 characters with no spaces, and include both letters and numbers.'
      } else if (key === 'phone') {
        errorString = 'A valid phone number is required.'
      }
    } else {
      errorString = 'Uh Oh, it looks like we need some more information. Complete the required fields to continue: '
      requiredErrors.forEach(({ label }, ix) => {
        errorString += label
        if (ix !== requiredErrors.length - 1) errorString += ', '
      })
    }

    return isSubmitted && errorString ?
      <FormGroup.HelperText
        className={styles.formError}
        data-testid="namestep-validation-error"
      >
        {errorString}
      </FormGroup.HelperText>
      : null
  }

  const commonInputProps = {
    labelProps: { hidden: true },
    shouldDisplayErrorMessage: false,
    size: 'large',
    helperProps: {
      className: styles.helperMessage,
    },
  }

  return (
    <SignUpLayout sideContent={plan?.sku === 'neathomeyr' || plan?.sku === 'neathomevipyr' || plan?.sku === NEAT_HOME_TRIAL_SKU ? <CheckoutSidePasswordNeatHome /> : <CheckoutSidePassword />}>
      <FormBox>
        <h1 className={styles.headline}>
          Welcome to Neat
        </h1>
        <p className="text-center">Let's finish setting up your account.</p>
        <Form
          initialValues={{ password: '', firstName: user.firstName ?? '', lastName: user.lastName ?? '', phone: '' }}
          onSubmit={saveData}
          schema={schema}
        >
          <Rows gap={0}>
            <Columns gap={8} noMobileGap>
              <Column>
                <InputField
                  label="First Name"
                  name="firstName"
                  {...commonInputProps}
                  data-testid="first-name-input-field"
                />
              </Column>
              <Column>
                <InputField
                  label="Last Name"
                  name="lastName"
                  {...commonInputProps}
                  data-testid="last-name-input-field"
                />
              </Column>
            </Columns>
            <Column>
              <div>
                <PhoneNumberField
                  label="Mobile Number"
                  name="phone"
                  {...commonInputProps}
                  data-testid="mobile-number-input-field"
                  helperMessage="Used by customer support to identify your account"
                />
              </div>
            </Column>
            {
                !user.googleId &&
                <Column>
                  <PasswordInputField
                    helperMessage="At least 9 characters, no spaces, include both letters and numbers."
                    helperProps={{ className: styles.helperMessage }}
                  />
                </Column>
              }
            <FormError />
            <Form.Action
              className={styles.submit}
              data-testid="finish-cta"
              messageId="pages.signUp.form.newUser.nextButton"
              size="large"
              type="submit"
            />
          </Rows>
        </Form>
      </FormBox>
    </SignUpLayout>
  )
}

export default NewUser
